import axios from 'axios'

// Scale API base URL - hardcoded for CAR backend
// const SCALE_API_BASE_URL = 'https://car-scalemediation-be.onrender.com'
const SCALE_API_BASE_URL =
    process.env.REACT_APP_CAR_BACKEND_API ||
    'https://car-scalemediation-be.onrender.com'
const SCALE_API_PREFIX = '/api/v1/scale'

// API key - hardcoded (update this with your actual API key)
// const SCALE_API_KEY = 'your-secret-api-key-here'

// Create axios instance for Scale API with X-API-Key header
const ScaleApiService = axios.create({
    baseURL: SCALE_API_BASE_URL,
    timeout: 60000,
})

// Request interceptor to add API key header
ScaleApiService.interceptors.request.use(
    (config) => {
        // if (SCALE_API_KEY) {
        //     config.headers['X-API-Key'] = SCALE_API_KEY
        // }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor for error handling
ScaleApiService.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        return Promise.reject(error)
    }
)

/**
 * Get pending cases for a mediator (Awaiting Approval)
 * @param {string} mediatorExternalId - The mediator's external ID from Scale
 * @returns {Promise} Pending cases data
 */
export const getPendingCases = async (mediatorExternalId) => {
    try {
        const response = await ScaleApiService.get(
            `${SCALE_API_PREFIX}/cases/pending`,
            {
                params: {
                    mediator_external_id: mediatorExternalId,
                },
            }
        )
        return {
            success: true,
            data: response.data?.data || [],
        }
    } catch (error) {
        console.error('Error fetching pending cases:', error)
        throw error
    }
}

/**
 * Get case details by case ID or number
 * @param {string} caseIdOrNumber - Case ID or case number
 * @param {string} mediatorExternalId - The mediator's external ID from Scale
 * @returns {Promise} Case details
 */
export const getCaseDetails = async (caseIdOrNumber, mediatorExternalId) => {
    try {
        const response = await ScaleApiService.get(
            `${SCALE_API_PREFIX}/cases/${caseIdOrNumber}`,
            {
                params: {
                    mediator_external_id: mediatorExternalId,
                },
            }
        )
        return {
            success: true,
            data: response.data?.data || null,
        }
    } catch (error) {
        console.error('Error fetching case details:', error)
        throw error
    }
}

/**
 * Get case details by case ID from /api/v1/case/details/{caseId}
 * @param {string} caseId - Case ID
 * @returns {Promise} Case details
 */
export const getCaseDetailsById = async (caseId) => {
    try {
        const response = await ScaleApiService.get(
            `/api/v1/case/details/${caseId}`
        )
        return {
            success: true,
            data: response.data?.data || response.data || null,
        }
    } catch (error) {
        console.error('Error fetching case details by ID:', error)
        throw error
    }
}

/**
 * Submit mediator decision (approve or reject)
 * @param {Object} decisionData - Decision data
 * @param {string} decisionData.case_id - Case ID (or use case_number)
 * @param {string} decisionData.case_number - Case number (or use case_id)
 * @param {string} decisionData.mediator_external_id - Mediator external ID
 * @param {string} decisionData.decision - 'approve' or 'reject'
 * @param {string} decisionData.rejection_reason - Optional rejection reason
 * @returns {Promise} Decision response
 */
export const submitMediatorDecision = async (decisionData) => {
    try {
        const response = await ScaleApiService.post(
            `${SCALE_API_PREFIX}/mediator-decision`,
            decisionData
        )
        return {
            success: true,
            data: response.data?.data || response.data,
        }
    } catch (error) {
        console.error('Error submitting mediator decision:', error)
        throw error
    }
}

/**
 * Update CAR case status in the external CAR portal
 * @param {Object} statusData - Status update data
 * @param {string} statusData.case_id - Case ID
 * @param {string} statusData.case_number - Case number (optional)
 * @param {string} statusData.mediator_external_id - Mediator external ID
 * @param {string} statusData.status - New status (e.g., 'scheduled', 'upcoming')
 * @returns {Promise} Status update response
 */
export const updateCarCaseStatus = async (statusData) => {
    try {
        const response = await ScaleApiService.post(
            `${SCALE_API_PREFIX}/case-status`,
            statusData
        )
        return {
            success: true,
            data: response.data?.data || response.data,
        }
    } catch (error) {
        console.error('Error updating CAR case status:', error)
        throw error
    }
}

/**
 * Get sync status
 * @returns {Promise} Sync status data
 */
export const getSyncStatus = async () => {
    try {
        const response = await ScaleApiService.get(
            `${SCALE_API_PREFIX}/sync-status`
        )
        return {
            success: true,
            data: response.data?.data || response.data,
        }
    } catch (error) {
        console.error('Error fetching sync status:', error)
        throw error
    }
}

/**
 * Sync mediators from Scale
 * @returns {Promise} Sync result
 */
export const syncMediators = async () => {
    try {
        const response = await ScaleApiService.get(
            `${SCALE_API_PREFIX}/sync-mediators`
        )
        return {
            success: true,
            data: response.data?.data || response.data,
        }
    } catch (error) {
        console.error('Error syncing mediators:', error)
        throw error
    }
}

/**
 * Health check (no auth required)
 * @returns {Promise} Health status
 */
export const healthCheck = async () => {
    try {
        const response = await ScaleApiService.get(`${SCALE_API_PREFIX}/health`)
        return {
            success: true,
            data: response.data,
        }
    } catch (error) {
        console.error('Error checking health:', error)
        throw error
    }
}

/**
 * Direct schedule case via CAR backend API
 * @param {Object} scheduleData - Schedule data
 * @param {string} scheduleData.case_id - Case ID
 * @param {string} scheduleData.final_date - Selected date (YYYY-MM-DD)
 * @param {string} scheduleData.final_slot - Selected slot type ('morning', 'afternoon', 'fullday')
 * @param {string} scheduleData.mediator_external_uuid - Mediator external UUID
 * @returns {Promise} Schedule response
 */
export const directScheduleCase = async (scheduleData) => {
    try {
        const caseId = scheduleData.case_id
        const slot = scheduleData.final_slot === 'fullday' ? 'full' : scheduleData.final_slot
        
        const payload = {
            mediator_id: scheduleData.mediator_external_uuid,
            date: scheduleData.final_date,
            slot: slot, // 'morning' | 'afternoon' | 'full'
        }

        const response = await ScaleApiService.post(
            `/api/v1/mediation-schedule/${caseId}/direct-schedule`,
            payload
        )

        return {
            success: response.data?.success || true,
            data: response.data?.data || response.data,
        }
    } catch (error) {
        console.error('Error in direct schedule case:', error)
        throw error
    }
}

export default {
    getPendingCases,
    getCaseDetails,
    getCaseDetailsById,
    submitMediatorDecision,
    updateCarCaseStatus,
    getSyncStatus,
    syncMediators,
    healthCheck,
    directScheduleCase,
}
